import React from "react";
import { Link } from "react-router-dom";
import SEOMeta from "../components/SEOMeta";

const Home: React.FC = () => {
  const canonicalUrl = "https://cyberannals.com";

  return (
    <>
      <SEOMeta
        title="CyberAnnals: Complete History of Cyber Attacks"
        description="Explore the complete history of cyber security events from ancient times to 2025. Detailed case studies of major cyber attacks."
        keywords="cyberannals, cyberannals.com, cyberAnnals, cyberAnnals.com, Cyberannals.com, Cyberannals, CyberAnnals, CyberAnnals.com, cyber,virus,attack,DDos, cyber attacks, cyber attack, recent cyber attacks, cyber attack news, cyberattack, cyber breach, cyber crime, cybercrime, cyber crimes, internet security, cyber awareness, scytale, caesar cipher, vigenere cipher, Great Seal Bug, great seal, bug, Cambridge,   Cambridge Time-Sharing Heist, Heist, Morris Worm, morris, Operation Sunburst, Bronze Soldier Riots, Stuxnet,Equifax, Equifax Catastrophe, Colonial Pipeline ransomware attack, colonial pipeline attack, colonial pipeline,cybersecurity historical timeline, cyber attacks database, data breach archive, ransomware history, cyber warfare evolution, hacking incidents timeline"
        canonical={canonicalUrl}
        ogImage="https://cyberannals.com/cyberAnnals_logo.png"
      />

      {/* Hidden JSON-LD for homepage */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "CyberAnnals",
            description:
              "Historical database of cyber security events and attacks",
            url: canonicalUrl,
            potentialAction: {
              "@type": "SearchAction",
              target: `${canonicalUrl}/search?q={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          }),
        }}
        style={{ display: "none" }}
      />
      <div className="space-y-12 sm:space-y-14 py-2 sm:py-4 md:py-6 animate-in fade-in duration-1000 relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.02] -z-10 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-600 to-transparent"></div>
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Cpath d='M0 0h80v80H0z' fill='none'/%3E%3Cpath d='M10 10h60v60H10z' stroke='%232563eb' fill='none' stroke-width='1'/%3E%3Ccircle cx='40' cy='40' r='5' fill='%232563eb' opacity='0.5'/%3E%3C/svg%3E")`,
              backgroundSize: "80px 80px",
            }}
          ></div>
        </div>

        {/* Hero Section */}
        <section className="text-center space-y-12 sm:space-y-8 max-w-5xl mx-auto px-2 relative">
          <div className="space-y-2">
            <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.9] sm:leading-[0.8] italic uppercase text-slate-900 dark:text-white">
              History of <br />
              <span className="bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 bg-clip-text text-transparent">
                Cyber Security
              </span>
            </h1>
          </div>
          <p className="text-xs sm:text-sm md:text-xl text-slate-700 dark:text-slate-400 leading-relaxed max-w-3xl mx-auto font-bold tracking-tight px-4 sm:px-0">
            The definitive archive documenting the{" "}
            <span>
              history of cyber security attacks
            </span>{" "}
            and the relentless{" "}
            <span>
              evolution of defense systems
            </span>{" "}
            from ancient ciphers to modern AI.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 pt-2 sm:pt-4 px-4">
            <Link
              to="/decades"
              className="w-full sm:w-auto px-10 py-4 sm:px-12 sm:py-5 border-2 border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-xl sm:rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] text-slate-900 dark:text-white transition-all transform hover:-translate-y-1 text-center"
            >
              Explore The Annals
            </Link>
            <Link
              to="/toolkit"
              className="w-full sm:w-auto px-10 py-4 sm:px-12 sm:py-5 border-2 border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-xl sm:rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] text-slate-900 dark:text-white transition-all transform hover:-translate-y-1 text-center"
            >
              Defensive Toolkit
            </Link>
          </div>
        </section>

        {/* Feature Grids */}
        <section className="max-w-7xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-12">
          <div className="space-y-4 sm:space-y-6 p-6 sm:p-8 border-l-4 border-blue-600 bg-slate-50 dark:bg-slate-900/30 rounded-r-2xl sm:rounded-r-3xl">
            <h2 className="text-xl sm:text-2xl font-black italic uppercase tracking-tighter text-slate-900 dark:text-white">
              What is History?
            </h2>
            <p className="text-xs sm:text-sm font-bold leading-relaxed text-slate-600 dark:text-slate-400 italic">
              History is not merely a list of dates; it is the collective
              intelligence of the human race. In the{" "}
              <span >
                Temporal Sector
              </span>
              , history represents the verified chronicle of successes and
              failures.
            </p>
          </div>
          <div className="space-y-4 sm:space-y-6 p-6 sm:p-8 border-l-4 border-indigo-600 bg-slate-50 dark:bg-slate-900/30 rounded-r-2xl sm:rounded-r-3xl">
            <h2 className="text-xl sm:text-2xl font-black italic uppercase tracking-tighter text-slate-900 dark:text-white">
              Why History?
            </h2>
            <p className="text-xs sm:text-sm font-bold leading-relaxed text-slate-600 dark:text-slate-400 italic">
              To ignore history is to invite catastrophe. We archive the{" "}
              <span >
                Eras of Cyber Security
              </span>{" "}
              to provide a roadmap for survival. Understanding the past is the
              only way to predict the future.
            </p>
          </div>
          <div className="space-y-4 sm:space-y-6 p-6 sm:p-8 border-l-4 border-purple-600 bg-slate-50 dark:bg-slate-900/30 rounded-r-2xl sm:rounded-r-3xl sm:col-span-2 lg:col-span-1">
            <h2 className="text-xl sm:text-2xl font-black italic uppercase tracking-tighter text-slate-900 dark:text-white">
              Why Breach History?
            </h2>
            <p className="text-xs sm:text-sm font-bold leading-relaxed text-slate-600 dark:text-slate-400 italic">
              The{" "}
              <span >
                History of Cyber Attacks
              </span>{" "}
              and the{" "}
              <span >
                History of Cyber Security Events
              </span>{" "}
              are the blueprints for modern defense. From analyzing the{" "}
              <span >
                History of Cyber Crime
              </span>{" "}
              o the cultural impact of the{" "}
              <span >
                History of Cyberpunk
              </span>{" "}
              and the sociological lessons from the{" "}
              <span >
                {" "}
                History of Cyberbullying,
              </span>{" "}
              we decode the patterns of aggression to build better walls.
            </p>
          </div>
        </section>

        {/* High-Impact Philosophy Block */}
        <section className="max-w-7xl mx-auto px-4">
          <div className="bg-slate-950 text-white rounded-[2rem] sm:rounded-[4rem] p-8 sm:p-12 md:p-20 relative overflow-hidden border border-white/5 shadow-2xl">
            <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none hidden md:block">
              <i className="fa-solid fa-shield-virus text-[200px]"></i>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 relative z-10">
              <div className="space-y-6 sm:space-y-8">
                <h2 className="text-xl sm:text-3xl font-black italic uppercase tracking-tighter text-slate-900 dark:text-white">
                  Evolution of Defense Systems
                </h2>
                <div className="text-xs sm:text-sm font-bold leading-relaxed text-slate-600 dark:text-slate-400 italic">
                  <p>
                    Analyzing the{" "}
                    <span className="font-bold ">
                      history of cyber security
                    </span>{" "}
                    reveals a constant arms race. Every advance in the{" "}
                    <span className="font-bold ">
                      history of cyber security
                    </span>{" "}
                    has been met with a more sophisticated evolution in
                    defensive protocols. We document the transition from
                    physical ciphers to the{" "}
                    <span className="font-bold ">
                      history of cyber security events
                    </span>{" "}
                    that led to the birth of the modern firewall and Zero Trust
                    architectures.
                  </p>
                </div>
              </div>

              <div className="space-y-6 sm:space-y-8">
                <h2 className="text-xl sm:text-3xl font-black italic uppercase tracking-tighter text-slate-900 dark:text-white">
                  Archives of Cyber Crime
                </h2>
                <div className="text-xs sm:text-sm font-bold leading-relaxed text-slate-600 dark:text-slate-400 italic">
                  <p>
                    <span>
                      The history of cyber crime
                    </span>{" "}
                    is a mirror of societal shift. As humanity moved online, so
                    did the predator. By indexing the{" "}
                    <span>
                      history of cyber security attacks,
                    </span>{" "}
                    CyberAnnals is the definitive chronicle of cyber
                    threat-actor psychology—past, present, and evolving.{" "}
                    <span>
                      The history of cyber crime
                    </span>{" "}
                    repository for the global intelligence community.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Feature Icons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 px-4 max-w-7xl mx-auto">
          {[
            {
              icon: "fa-landmark-flag",
              color: "blue",
              title: "Ancient Ciphers",
              desc: "Explore cryptographic foundations long before the microchip.",
            },
            {
              icon: "fa-microchip",
              color: "indigo",
              title: "Digital Warfare",
              desc: "Comprehensive history of attacks that redefined international conflict.",
            },
            {
              icon: "fa-bolt-lightning",
              color: "purple",
              title: "AI Interdiction",
              desc: "The current era where defense systems evolve autonomously.",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="group p-8 sm:p-10 bg-white dark:bg-slate-900/50 backdrop-blur-sm border border-slate-200 dark:border-slate-800 rounded-[2rem] sm:rounded-[3rem] shadow-sm hover:shadow-2xl hover:border-blue-500/30 transition-all duration-500 relative overflow-hidden"
            >
              <div
                className={`w-12 h-12 sm:w-14 sm:h-14 bg-${item.color}-500/10 flex items-center justify-center rounded-xl sm:rounded-[1.2rem] mb-6 sm:mb-8 group-hover:scale-110 transition-transform`}
              >
                <i
                  className={`fa-solid ${item.icon} text-${item.color}-600 text-xl sm:text-2xl`}
                ></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-black mb-3 sm:mb-4 uppercase tracking-tighter italic text-slate-900 dark:text-white">
                {item.title}
              </h3>
              <p className="text-[12px] sm:text-[14px] text-slate-800 dark:text-slate-400 leading-relaxed font-bold opacity-80">
                {item.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Nodes Section */}
        <section className="space-y-8 sm:space-y-12 px-4 max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black text-center uppercase tracking-tighter italic text-slate-900 dark:text-white">
            Evolution of Cybersecurity
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { label: "Creeper", year: " 1971" },
              { label: "Morris Worm", year: "1988" },
              { label: "Cloud Breaches", year: "2020" },
              { label: "AI Warfare", year: "2025" },
            ].map((node) => (
              <Link
                key={node.label}
                to="/decades"
                className="group p-6 sm:p-10 text-center bg-slate-100 dark:bg-slate-900 border border-transparent hover:border-blue-600 rounded-2xl sm:rounded-[2.5rem] transition-all duration-300 shadow-sm overflow-hidden relative"
              >
                <span className="block text-lg sm:text-2xl font-black mb-1 sm:mb-2 italic tracking-tighter group-hover:text-blue-600 transition-colors text-slate-900 dark:text-white">
                  {node.label}
                </span>
                <span className="text-[8px] sm:text-[9px] uppercase font-black tracking-widest bg-blue-500/10 px-2 py-1 rounded-lg">
                  {node.year}
                </span>
                <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 opacity-5 group-hover:opacity-10 transition-opacity">
                  <i className="fa-solid fa-clock-rotate-left text-5xl sm:text-8xl"></i>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <div className="text-center pb-8 sm:pb-12 opacity-40">
          <p className="text-[8px] sm:text-[9px] font-black uppercase tracking-[0.5em] text-slate-500">
            History of Cyber Security Events •
          </p>
        </div>
      </div>
    </>
  );
};

export default Home;
